import { useEffect, useState } from "react";
import { createRole, deleteRole, fetchRoles, updateRole } from "../services/roleApi";
import { showErrorToast, showSuccessToast } from "../utils/toast";
import ConfirmationModal from "../components/CommonComponents/ConfirmationModal";
import { Loader2, Plus, Trash2, UserCog } from "lucide-react";
import { MdToggleOn, MdToggleOff } from "react-icons/md";
import { tableBase, tableCell, tableContainer, tableHead, tableHeader, tableRow } from "../utils/tableStyle";

export default function CustomerDetailsTab() {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [roleName, setRoleName] = useState("");
    const [confirmState, setConfirmState] = useState({ isOpen: false, roleId: null, roleName: "" });

    const loadRoles = async () => {
        setLoading(true);
        try {
            const res = await fetchRoles();
            setRoles(res.data.data || []);
        } catch (err) {
            console.error("Failed to fetch roles:", err);
            showErrorToast("Failed to load roles.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRoles();
    }, []);

    const handleAddRole = async (e) => {
        e.preventDefault();
        if (!roleName.trim()) return;
        try {
            await createRole({ name: roleName.trim() });
            setRoleName("");
            showSuccessToast("Role added.");
            loadRoles();
        } catch (err) {
            console.error("Failed to add role:", err);
            showErrorToast(err.response?.data?.message || "Failed to add role.");
        }
    };

    const handleDeleteRole = async (id) => {
        try {
            await deleteRole(id);
            showSuccessToast("Role deleted.");
            loadRoles();
        } catch (err) {
            console.error("Failed to delete role:", err);
            showErrorToast(err.response?.data?.message || "Failed to delete role.");
        }
    };

    const toggleRoleStatus = async (role) => {
        const nextStatus = role.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
        try {
            await updateRole(role.id, { name: role.name, status: nextStatus });
            showSuccessToast(`Role ${nextStatus === "ACTIVE" ? "activated" : "deactivated"}.`);
            loadRoles();
        } catch (err) {
            console.error("Failed to update role:", err);
            showErrorToast(err.response?.data?.message || "Failed to update role.");
        }
    };

    const openDeleteConfirm = (role) => {
        setConfirmState({ isOpen: true, roleId: role.id, roleName: role.name });
    };

    const closeDeleteConfirm = () => {
        setConfirmState({ isOpen: false, roleId: null, roleName: "" });
    };

    const confirmDelete = async () => {
        if (!confirmState.roleId) return;
        await handleDeleteRole(confirmState.roleId);
        closeDeleteConfirm();
    };

    return (
        <div className="rounded-2xl border p-5 shadow-sm" style={{ background: "var(--color-bg)", borderColor: "var(--color-border)" }}>
            <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: "var(--color-muted)" }}>
                        <UserCog className="h-4 w-4" />
                        Staff Access
                    </div>
                    <h2 className="mt-2 text-base font-semibold" style={{ color: "var(--color-text)" }}>Staff Account Roles</h2>
                    <p className="mt-1 text-sm" style={{ color: "var(--color-muted)" }}>
                        Manage the account roles available when creating cashier, supervisor, and manager staff users.
                    </p>
                </div>
                {loading && (
                    <span className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--color-muted)" }}>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading
                    </span>
                )}
            </div>

            <form onSubmit={handleAddRole} className="mb-6 grid gap-3 sm:grid-cols-[minmax(220px,320px)_auto] sm:items-end">
                <div className="flex flex-col min-w-[220px]">
                    <label className="mb-2 text-sm font-semibold" style={{ color: "var(--color-text)" }}>New Role</label>
                    <input
                        type="text"
                        value={roleName}
                        onChange={(e) => setRoleName(e.target.value)}
                        placeholder="e.g., Supervisor"
                        className="rounded-md border px-4 py-3 text-sm outline-none"
                        style={{ background: "var(--color-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
                    />
                </div>
                <button
                    type="submit"
                    className="btn-primary inline-flex items-center justify-center gap-2 rounded-md px-5 py-3 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={!roleName.trim()}
                >
                    <Plus className="h-4 w-4" />
                    Add Role
                </button>
            </form>

            {loading ? (
                <div className="text-sm" style={{ color: "var(--color-muted)" }}>Loading roles...</div>
            ) : roles.length === 0 ? (
                <div className="rounded-2xl border px-4 py-8 text-center text-sm" style={{ borderColor: "var(--color-border)", color: "var(--color-muted)" }}>
                    No roles added yet.
                </div>
            ) : (
                <div className={tableContainer} style={{ border: "1px solid var(--color-border)" }}>
                    <table className={tableBase}>
                        <thead className={tableHead} style={{ background: "var(--color-primary)" }}>
                            <tr>
                                <th className={tableHeader + " text-left"}>Role</th>
                                <th className={tableHeader + " text-left"}>Status</th>
                                <th className={tableHeader + " text-left"}>Created</th>
                                <th className={tableHeader + " text-right"}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {roles.map(role => (
                                <tr key={role.id} className={tableRow + " border-b last:border-0"} style={{ borderColor: "var(--color-border)" }}>
                                    <td className={tableCell} style={{ color: "var(--color-text)" }}>{role.name}</td>
                                    <td className={tableCell}>
                                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${role.status === "ACTIVE"
                                            ? "bg-green-100 text-green-700"
                                            : role.status === "INACTIVE"
                                                ? "bg-yellow-100 text-yellow-700"
                                                : "bg-gray-200 text-gray-600"}`}
                                        >{role.status || "ACTIVE"}</span>
                                    </td>
                                    <td className={tableCell} style={{ color: "var(--color-text)" }}>{role.createdAt ? new Date(role.createdAt).toLocaleString() : "-"}</td>
                                    <td className={tableCell + " text-right"}>
                                        <div className="flex items-center justify-end gap-3">
                                            <button
                                                type="button"
                                                onClick={() => toggleRoleStatus(role)}
                                                title={role.status === "ACTIVE" ? "Deactivate" : "Activate"}
                                                className="transition hover:scale-105"
                                            >
                                                {role.status === "ACTIVE" ? (
                                                    <MdToggleOn size={30} className="text-green-500" />
                                                ) : (
                                                    <MdToggleOff size={30} className="text-gray-400" />
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => openDeleteConfirm(role)}
                                                className="rounded-md p-2 text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600"
                                                title="Delete role"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <ConfirmationModal
                isOpen={confirmState.isOpen}
                onClose={closeDeleteConfirm}
                onConfirm={confirmDelete}
                title="Delete role?"
                message={confirmState.roleName ? `Delete ${confirmState.roleName}?` : "Delete this role?"}
                confirmText="Delete"
                cancelText="Cancel"
                type="delete"
            />
        </div>
    );
}
